import React, { useEffect, useState } from 'react';
import useInitialEligibleItems from '@src/hooks/useInitialEligibleItems';
import { useReturnStore } from '@src/stores/returnStore';
import { goToNextStep } from '@src/stores/newFlowStore';
import useScrollableError from '@src/hooks/useScrollableError';
import {
  redirectToOrderDetails,
  getFeatureSwitchStatic,
} from '@src/utils/general';
import { RETURN_METHOD } from '@src/constants/general';
import { TagReturnMethodLoad } from '@src/utils/analytics-tagging';
import { ReturnHeaders } from '../shared/ReturnHeaders';
import SelectItem from './SelectItem';
import SelectIneligibleItems from './SelectIneligibleItems';
import { ReturnButtons } from '../shared/ReturnButtons';
import ZeroItemsError from './errors/ZeroItemsError';
import InStoreReturnModal from '../shared/InStoreReturnModal';

const SelectItemsToReturn = () => {
  const orderDetailsV2 = useReturnStore((state) => state.orderDetailsV2);
  const [showInStoreModal, setShowInStoreModal] = useState(true);
  const isMilitaryExchangeGuest = useReturnStore(
    (state) => state.isMilitaryExchangeGuest,
  );
  const orderDetailsPath = useReturnStore((state) => state.orderDetailsPath);
  const returnEligibilityDetails = useReturnStore(
    (state) => state.returnEligibilityDetails,
  );
  const selectedItems = useReturnStore((state) => state.selectedItems);
  const isApoFpo = useReturnStore((state) => state.isApoFpo);
  const { eligibleItems, ineligibleItems } = useInitialEligibleItems(
    orderDetailsV2,
    returnEligibilityDetails,
    isMilitaryExchangeGuest && Boolean(isApoFpo),
  );
  const allowSORIS = getFeatureSwitchStatic('allowSORIS');
  const { errorRef, showAndScrollToError, showError, hideScrollableError } =
    useScrollableError();

  // Track page load when user first lands on Return Items flow
  useEffect(() => {
    TagReturnMethodLoad(isMilitaryExchangeGuest ? 'nex' : undefined);
  }, []);

  const handleContinueButton = () => {
    if (selectedItems.length === 0) {
      showAndScrollToError();
      return;
    }

    // SORIS all-or-nothing is applied at render on the method screens
    // (OrderReturnMethodOptions and SplitReturnMethodOptions both derive it via
    // selectedItems.every(SORIS)), so no eligibility mutation is needed here.
    goToNextStep();
  };

  const handleReturnInStore = () => {
    redirectToOrderDetails(orderDetailsPath, isMilitaryExchangeGuest);
  };

  const allItemsSorisEligible = (items: typeof selectedItems) =>
    allowSORIS &&
    items.length > 0 &&
    items.every((item) => item.eligibilities?.[RETURN_METHOD.SORIS] === true);

  useEffect(() => {
    if (selectedItems.length > 0) {
      hideScrollableError();
    }
  }, [selectedItems, hideScrollableError]);

  return (
    <div>
      {showInStoreModal &&
        !allItemsSorisEligible(eligibleItems) &&
        !isApoFpo && (
          <InStoreReturnModal
            onClose={() => setShowInStoreModal(false)}
            onReturnInStore={handleReturnInStore}
            onSelectOtherOptions={() => setShowInStoreModal(false)}
          />
        )}
      <ReturnHeaders title="Select items to return" />
      <ZeroItemsError visible={showError} ref={errorRef} />
      <div className="sui-flex sui-flex-col sui-gap-4">
        {eligibleItems.map((item) => (
          <SelectItem key={item.lineItemId} item={item} />
        ))}
      </div>
      {ineligibleItems.length > 0 && (
        <SelectIneligibleItems ineligibleItems={ineligibleItems} />
      )}
      <ReturnButtons proceedNext={handleContinueButton} />
    </div>
  );
};

export default SelectItemsToReturn;
